import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import TabbedPageShell from '@/components/TabbedPageShell';
import MyHealthTab from '@/components/health/MyHealthTab';
import WellbeingTab from '@/components/health/WellbeingTab';
import HealthHeroCard from '@/components/health/HealthHeroCard';
import SectionShortcutsLauncher from '@/components/SectionShortcutsLauncher';
import SectionNudge from '@/components/SectionNudge';
import SectionGuideRail from '@/components/playbooks/SectionGuideRail';
import { useApp } from '@/contexts/AppContext';

const TABS = [
  {
    key: 'health-wellbeing',
    label: 'Health & wellbeing',
    subtitle: 'Appointments, medications, screenings, check-ins, and support',
    content: null,
  },
];

export default function HealthPage() {
  const navigate = useNavigate();
  const { data } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryTab = searchParams.get('tab');
  const queryView = searchParams.get('view');
  const launchAction = searchParams.get('compose') as 'reminder' | 'medication' | 'pet-care' | null;
  const hasTrackedPets = (data.pets ?? []).some((pet) => pet.name.trim().length > 0);

  useEffect(() => {
    if (queryTab !== 'community') {
      return;
    }

    navigate(`/community${queryView ? `?view=${queryView}` : ''}`, { replace: true });
  }, [navigate, queryTab, queryView]);

  const openHealthShortcut = useCallback((compose: 'reminder' | 'medication' | 'pet-care') => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'my-health');
    nextParams.set('compose', compose);
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const clearHealthShortcut = useCallback(() => {
    if (!searchParams.get('compose')) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('compose');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <TabbedPageShell
      title="My Health"
      subtitle="Stay on top of my health and wellbeing — reminders, check-ins, support, and my NHS"
      tabs={[
        {
          ...TABS[0],
          content: (
            <div className="space-y-4">
              <MyHealthTab launchAction={launchAction} onLaunchHandled={clearHealthShortcut} />
              <WellbeingTab />
            </div>
          ),
        },
      ]}
      nudge={<SectionNudge section="health" />}
      hero={(
        <HealthHeroCard
          action={(
            <SectionShortcutsLauncher
              title="Health shortcuts"
              subtitle="Start the next health admin job without leaving a bulky panel on screen."
              items={[
                {
                  id: 'reminder',
                  label: 'Add reminder',
                  description: 'Save the next appointment, screening, or review date.',
                  onPress: () => openHealthShortcut('reminder'),
                },
                {
                  id: 'medication',
                  label: 'Add medication',
                  description: 'Track a repeat prescription or medication routine.',
                  onPress: () => openHealthShortcut('medication'),
                },
                {
                  id: 'pet-care',
                  label: 'Log pet care',
                  description: 'Record the next vet, treatment, or grooming milestone.',
                  onPress: () => openHealthShortcut('pet-care'),
                  hidden: !hasTrackedPets,
                },
              ]}
            />
          )}
        />
      )}
      guideRail={<SectionGuideRail section="health" />}
    />
  );
}
